import { getBrowserInfo } from "@/utils/BrowserInfo";
import { getBookingStatus } from "@/utils/Formatting";
import { pb, UsersRecord } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { useRoute } from "vue-router";
import { useListStore } from "./listStore";

export const useCalendarStore = defineStore("calendar", () => {
  // store
  const list = useListStore();

  const { getListArea, fetchActivities: fetchActivityList } = list;

  const { listArea: areasList, activityList } = storeToRefs(list);

  const route = useRoute()
  // data
  const bookingList = ref<any>();
  const availableCalendars = ref<any>([]);
  const selectedCalendars = ref<any>([]);
  const isBookingStepperVisible = ref<boolean>(false);
  const isSimplerBookingStepperVisible = ref<boolean>(false);
  const isConfirmEditBookingDialog = ref<boolean>(false);
  const editBookingData = ref<any>("");
  const usersList = ref<UsersRecord[]>([]);
  const areaList = ref<any[]>([]);

  const bookingStatusList = ref([
    {
      color: "grey",
      name: "Show Completed",
      value: "completed",
    },
    {
      color: "green",
      name: "Show In Progress",
      value: "inprogress",
    },
  ]);

  const selectedBookingStatus = ref<any>([]);
  const currentViewType = ref<string>("");
  const calendarUpdateKey = ref<number>(1);
  const visibleStartDate = ref<any>();
  const recurringDetails = ref<any>({});
  const visibleRange = ref<any>({});

  // computed
  const getBookingList = computed(() => {
    // Filter booking based on selected calendars
    const filteredBookings =
      bookingList.value?.filter((el: any) =>
        selectedCalendars?.value?.includes(el.activity),
      ) || [];

    // Further filter based on view type and status
    if (currentViewType.value === "listMonth") {
      const today = moment().startOf("day"); // Start of today

      // Filter bookings that start today or in the future
      const filteredByDate = filteredBookings.filter((el: any) => {
        const startDate = moment(el.calendar.startDate).startOf("day"); // Start of booking's startDate

        return startDate.isSameOrAfter(today); // Include today or future dates
      });

      // Further filter based on selected booking status
      if (selectedBookingStatus.value.length) {
        return filteredByDate.filter(
          (el: any) =>
            selectedBookingStatus.value.includes(el.calendar.status) ||
            el.calendar.status === "futureBooking",
        );
      }

      return filteredByDate?.filter(
        (booking: any) => booking?.calendar?.status === "futureBooking",
      );
    }

    // Filter by selected booking status for other views
    if (selectedBookingStatus.value.length) {
      return filteredBookings.filter(
        (el: any) =>
          selectedBookingStatus.value.includes(el.calendar.status) ||
          el.calendar.status === "futureBooking",
      );
    }

    // no selection on side status
    return filteredBookings?.filter(
      (booking: any) => booking?.calendar?.status === "futureBooking",
    );
  });

  // Methods
  const fetchBookings = async () => {
    try {
      const objectMap = availableCalendars.value.reduce(
        (acc: any, item: any) => {
          acc[item.id] = item;

          return acc;
        },
        {},
      );

      // Activity list of selected location
      const activitiesList = availableCalendars.value?.map(
        (el: any) => `activity="${el.id}"`,
      );

      const filter = `(${activitiesList.join(" || ")}) && startTime <= '${
        visibleRange.value.endDate
      }' && endTime >= '${visibleRange.value.startDate}' && state != 'Deleted'`;

      const bookings = await pb.collection("bookings").getFullList({
        filter,
        expand: "users,area,activity,docketLine",
      });

      if (bookings) {
        const bookingIds = bookings.map((booking) => booking.id);

        const [recurringBookingsList, eventsList] = bookingIds.length
          ? await Promise.all([
              pb.collection("recurringBookings").getFullList({
                filter: bookingIds
                  .map((id) => `bookings ~ '${id}'`)
                  .join(" || "),
              }),
              pb.collection("events").getFullList({
                filter: bookingIds
                  .map((id) => `bookings ~ '${id}'`)
                  .join(" || "),
                expand:
                  "bookings.owner,bookings.docketLine.docket,bookings.area", // Ensure to expand all necessary fields
              }),
            ])
          : [[], []];

        const recurringEventsList = eventsList.length
          ? await pb.collection("recurringEvents").getFullList({
              filter: eventsList
                .map((event) => `events ~ '${event.id}'`)
                .join(" || "),
              expand: "events.bookings.docketLine.docket",
            })
          : [];

        // Map bookings with user names and recurring booking details
        bookings.map((el) => {
          // Create user names
          const userNames =
            el.expand?.users?.map(
              (user: any) => `${user.firstName} ${user.lastName}`,
            ) || [];

          el.title = userNames.join(", ");
          el.start = el.startTime;
          el.end = moment(el.startTime)
            .clone()
            .add(el.duration, "minutes")
            .toISOString();
          el.calendar = {
            eventColor: objectMap[el?.activity]?.color,
            activityName: objectMap[el?.activity]?.name,
            due: el.due,
            id: el.id,
            startDate: el.startTime,
            endTime: moment(el.startTime)
              .add(el.duration, "minutes")
              .toISOString(),
            duration: el.duration,
            statusClass: getBookingStatus(
              el.actualStartTime,
              el.actualDuration,
            ),
            status: getBookingStatus(el.actualStartTime, el.actualDuration),
          };

          // Check for recurring bookings
          const matchedRecurring = recurringBookingsList?.find((recurring) =>
            recurring?.bookings?.includes(el.id),
          );

          el.calendar.recurringBookingDetails = matchedRecurring || {};

          // Check for event details
          const matchedEvent = eventsList?.find((event) =>
            event?.bookings?.includes(el.id),
          );

          el.calendar.eventDetails = matchedEvent || {};

          // Check for recurring event details within the expanded events
          const matchedRecurringEvent = recurringEventsList?.find(
            (recurringEvent) =>
              recurringEvent.expand?.events?.some((event: any) =>
                event.expand?.bookings?.some(
                  (booking: any) => booking.id === el.id,
                ),
              ),
          );

          el.calendar.recurringEventDetails = matchedRecurringEvent || {};
        });

        bookingList.value = bookings;

        // Trigger calendar update after a delay
        setTimeout(() => {
          calendarUpdateKey.value++;
        }, 2000);
      }
    } catch (e) {}
  };

  const fetchActivities = async () => {
    try {
      await fetchActivityList();
      availableCalendars.value = activityList.value;
      selectedCalendars.value = availableCalendars.value.map(
        (el: any) => el.id,
      );

      return activityList.value;
    } catch (e) {}
  };

  const getUserList = async (force = false) => {
    try {
      if (!force && usersList.value.length) return;

      usersList.value = await pb.collection("users").getFullList({
        filter: 'status != "Suspended"',
        expand: "bankCards_via_user, membership",
      });
    } catch (e) {
      console.error("Error fetching users list:", e);
    }
  };

  const getAreasList = async () => {
    try {
      await getListArea();
      areaList.value = areasList.value;
    } catch (e) {}
  };

  const deleteBooking = async ({
    bookingId,
    recurringId,
    isAll,
  }: {
    bookingId: string;
    recurringId?: string;
    isAll: boolean;
  }) => {
    if (!bookingId && !recurringId) return;
    try {
      const processBooking = async (booking: any) => {
        // Get docketlines and update the booking state to Deleted
        const docketLines = await pb.collection("docketLines").getFullList({
          filter: 'booking="' + booking.id + '"',
          expand: "docket",
        })
        
        await pb.collection("bookings").update(booking.id, {
          state: "Deleted",
          value: 0,
          options:[],
        });

        // loop over docketlines and credit back to user coupons or account credit if they paid with those methods
        if (docketLines && docketLines.length > 0) {
          const docketMap = docketLines.map(dl => 'docket="' + dl.docket + '"').join('||');
          try {
            const payments = await pb.collection("docketPayments").getFullList({
              filter: 'paymentType="Coupon" && (' + docketMap + ')', expand:"coupon, docket"
            })
            if(payments){
              for(const payment of payments){
                if(payment?.expand?.coupon && payment?.amount > 0){
                  let qty = payment?.couponQty > 0 ? payment?.couponQty : 1
                  let amount = payment?.amount || 0
                  let coupon = payment?.expand?.coupon;
                  let docketStatus = payment?.expand?.docket?.status;
                  let docketStillOpen = docketStatus !== "Paid" && docketStatus !== "Closed";

                  // If a docket is still open, we can reuse it for the refund.
                  // If it's closed, we need to create a new docket for the refund. 
                  // Coupon qtyRemaining is credited back automatically by the
                  // Dockets.pb.js hook when the refund docketPayment below is created
                  let targetDocketId = payment.docket;
                  if (!docketStillOpen) {
                    let docket = await pb.collection("dockets").create({
                      user: coupon.user,
                      location: payment?.expand?.docket?.location,
                      createdBy: payment?.expand?.docket?.createdBy,
                      source: "Booking",
                    })
                    targetDocketId = docket.id;
                  }

                  // CREATE DOCKET LINE - Joins the coupons to a docket - docketLines Table
                  let docketLineToMake = {
                    couponQty: Math.floor(qty * -1),
                    coupon: coupon.id,
                    docket: targetDocketId,
                    updatedBy: payment?.expand?.docket?.createdBy,
                    transactionType: 'Booking Payment',
                    qty: Math.floor(qty * -1),
                    itemPrice: parseFloat((amount / Math.floor(qty)).toFixed(2)), // Docket Value
                    lineTotal: amount * -1, // Docket Value
                    status: 'Available',
                    booking: booking.id,
                    createdBy: payment?.expand?.docket?.createdBy,
                    lineText: "Coupon Refunded for Cancelled Booking"
                  };

                  await pb.collection("docketLines").create(docketLineToMake);
                  const browserInfo = await getBrowserInfo();

                  let finalCouponForDocketPayment = {
                    docket: targetDocketId,
                    paymentType: "Coupon",
                    status: "Approved",
                    amount: amount * -1,
                    user: payment?.expand?.coupon?.user,
                    coupon: coupon.id,
                    couponQty: Math.floor(qty * -1),
                    notes: "Coupon Refund for Cancelled Booking",
                    browser: browserInfo.browser,
                    OS: browserInfo.os,
                    ipAddress: browserInfo.ip,
                  }

                  await pb.collection("docketPayments").create(finalCouponForDocketPayment)
                }
              }
            }
          } catch(e){
            console.error('Error while refunding coupons', e)
          }

          // Credit Back to users Account the Amount of the booking
          try {
            const payments = await pb.collection("docketPayments").getFullList({
              filter: 'paymentType="User Account" && (' + docketMap + ')',
              expand: "docket"
            })
            if (payments) {
              for (const payment of payments) {
                if (payment?.amount > 0) {
                  let amount = payment?.amount || 0
                  let docketStatus = payment?.expand?.docket?.status;
                  let docketStillOpen = docketStatus !== "Paid" && docketStatus !== "Closed";

                  // If a docket is still open, we can reuse it for the refund.
                  // If it's closed, we need to create a new docket for the refund. 
                  let targetDocketId = payment.docket;
                  if (!docketStillOpen) {
                    let docket = await pb.collection("dockets").create({
                      user: payment?.user,
                      location: payment?.expand?.docket?.location,
                      createdBy: payment?.expand?.docket?.createdBy,
                      source: "Booking",
                    })
                    targetDocketId = docket.id;
                  }

                  // CREATE DOCKET LINE 
                  let docketLineToMake = {
                    docket: targetDocketId,
                    updatedBy: payment?.expand?.docket?.createdBy,
                    transactionType: 'Booking Payment',
                    qty: -1,
                    itemPrice: amount, // Docket Value
                    lineTotal: amount * -1, // Docket Value
                    status: 'Available',
                    booking: booking.id,
                    createdBy: payment?.expand?.docket?.createdBy,
                    lineText: "Account Refunded for Cancelled Booking"
                  };

                  await pb.collection("docketLines").create(docketLineToMake);
                  const browserInfo = await getBrowserInfo();

                  let finalCouponForDocketPayment = {
                    docket: targetDocketId,
                    paymentType: "User Account",
                    status: "Approved",
                    amount: amount * -1,
                    user: payment?.user,
                    notes: "Account Refund for Cancelled Booking",
                    browser: browserInfo.browser,
                    OS: browserInfo.os,
                    ipAddress: browserInfo.ip,
                  }

                  await pb.collection("docketPayments").create(finalCouponForDocketPayment)
                }
              }
            }
          } catch(e){
            console.error('Error while refunding user account', e)
          }

          // reverse charge for any due amount left on the booking for a open docket
          // This is a safety net in case the booking was partially paid with a coupon or user account and the rest was due on the docket
          try{
            let openDocket;
            for(const dl of docketLines){
              const docket = dl?.expand?.docket;
              if(docket?.status !== "Paid" && docket?.status !== "Closed"){
                openDocket = docket
              }
            }
            if(openDocket && booking.due > 0){
              await pb.collection("docketLines").create({
                docket: openDocket.id,
                transactionType: 'Booking Payment',
                qty: -1,
                itemPrice: booking.due, // Docket Value
                lineTotal: booking.due * -1, // Docket Value
                status: 'Available',
                booking: booking.id,
                lineText: "Cancel rest of booking due amount",
              })
            }
          }catch(e){
            console.error('Error while cancelling due amount', e)
          }
        }
      };

      try {
        if (recurringId && isAll) {
          const recurringBooking = await pb.collection("recurringBookings").getOne(recurringId, { expand: "bookings" });

          const bookings = recurringBooking?.expand?.bookings || [];

          await Promise.all(bookings.map(processBooking));
        } else {
          const { docketLine, startTime, id, due } = await pb.collection("bookings").getOne(bookingId);
          await processBooking({ id, docketLine, startTime, due });
        }
      } catch (error) {
        console.error("Error in if-else block:", error);
      }

      // Trigger calendar update after deletion
      setTimeout(() => {
        calendarUpdateKey.value++;
      }, 2000);

      isBookingStepperVisible.value = false;

    } catch (error) {
      console.error("Caught error in deleteBooking:", error);
      throw error;
    }

    // Refresh page if on POS
    if (route.name === "pos") {
      window.location.reload();
    }
  };

  const updateVisibleDateRange = (start: any, end: any) => {
    visibleRange.value = {
      startDate: moment(start)
        .startOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss"), // start of the day
      endDate: moment(end).endOf("day").utc().format("YYYY-MM-DD HH:mm:ss"), // end of the day
    };
  };

  // Hooks
  onMounted(async () => {
    await getUserList();
  });

  return {
    availableCalendars,
    selectedCalendars,
    getBookingList,
    editBookingData,
    usersList,
    areaList,
    bookingStatusList,
    selectedBookingStatus,
    currentViewType,
    calendarUpdateKey,
    visibleStartDate,
    isBookingStepperVisible,
    isSimplerBookingStepperVisible,
    recurringDetails,
    isConfirmEditBookingDialog,
    visibleRange,
    deleteBooking,
    fetchBookings,
    getAreasList,
    fetchActivities,
    updateVisibleDateRange,
    getUserList,
  };
});
